import { dictionaryApi } from './dictionaryClient';
import {
  normalizeAssociatedBarna,
  normalizePratyahar,
  type AssociatedBarna,
  type Pratyahar,
} from './pratyaharApi';

export type { AssociatedBarna };

export type PadachhedPratyaharLink = {
  id: number;
  ashtadhyayiPadachhedId: number;
  pratyaharId: number;
  selectedBarnas: AssociatedBarna[];
  pratyahar: Pratyahar | null;
};

export type CreatePadachhedPratyaharLinkBody = {
  ashtadhyayiPadachhedId: number;
  pratyaharId: number;
  selectedBarnas: AssociatedBarna[];
};

export type UpdatePadachhedPratyaharLinkBody = {
  selectedBarnas?: AssociatedBarna[];
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizePadachhedPratyaharLink(raw: unknown): PadachhedPratyaharLink | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiPadachhedId =
    num(raw, 'ashtadhyayiPadachhedId') ?? num(raw, 'ashtadhyayi_padachhed_id');
  const pratyaharId = num(raw, 'pratyaharId') ?? num(raw, 'pratyahar_id');
  if (id == null || ashtadhyayiPadachhedId == null || pratyaharId == null) return null;

  const selectedRaw = raw.selectedBarnas ?? raw.selected_barnas;
  const selectedBarnas = Array.isArray(selectedRaw)
    ? selectedRaw.map(normalizeAssociatedBarna).filter((x): x is AssociatedBarna => x != null)
    : [];

  const pratyaharRaw = raw.pratyahar;
  const pratyahar = pratyaharRaw ? normalizePratyahar(pratyaharRaw) : null;

  return {
    id,
    ashtadhyayiPadachhedId,
    pratyaharId,
    selectedBarnas,
    pratyahar,
  };
}

function normalizeList(data: unknown): PadachhedPratyaharLink[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizePadachhedPratyaharLink)
    .filter((x): x is PadachhedPratyaharLink => x != null);
}

export async function listPadachhedPratyaharLinks(filters?: {
  ashtadhyayiPadachhedId?: number;
  pratyaharId?: number;
}): Promise<PadachhedPratyaharLink[]> {
  const params: Record<string, number> = {};
  if (filters?.ashtadhyayiPadachhedId != null) {
    params.ashtadhyayiPadachhedId = filters.ashtadhyayiPadachhedId;
  }
  if (filters?.pratyaharId != null) {
    params.pratyaharId = filters.pratyaharId;
  }
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-padachhed-pratyahar', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return normalizeList(res.data);
}

export async function getPadachhedPratyaharLink(id: number): Promise<PadachhedPratyaharLink> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-padachhed-pratyahar/${id}`);
  const row = normalizePadachhedPratyaharLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–pratyahar link response');
  return row;
}

export async function createPadachhedPratyaharLink(
  body: CreatePadachhedPratyaharLinkBody
): Promise<PadachhedPratyaharLink> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-padachhed-pratyahar', body);
  const row = normalizePadachhedPratyaharLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–pratyahar link response');
  return row;
}

export async function updatePadachhedPratyaharLink(
  id: number,
  body: UpdatePadachhedPratyaharLinkBody
): Promise<PadachhedPratyaharLink> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-padachhed-pratyahar/${id}`, body);
  const row = normalizePadachhedPratyaharLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–pratyahar link response');
  return row;
}

export async function deletePadachhedPratyaharLink(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-padachhed-pratyahar/${id}`);
}
